'use client';

// BL-2473 — the one "pop each new toast once" loop. Extracted from the bidder's
// `CustomerToastListener` (BL-605) so the admin's staff mention pop-up runs the
// same subscription and dedupe rather than a copy of it (rule #18).
//
// LIVE-ONLY by design: `useLedger` runs with no initial fetch or reconcile, so a
// backlog is never re-shown on load. Each item reaches `onNew` exactly once per
// mount, deduped by id: a re-delivered frame must never pop twice.

import { useEffect, useRef } from 'react';

import { useLedger } from '../../components/realtime/LedgerContext';
import type { LedgerItem } from '../../stores/realtime/ledger-store';

/**
 * Subscribe to a toast ledger topic and hand each newly-arrived item to `onNew` once.
 *
 * The topic must be non-empty: callers gate on a minted topic before mounting
 * (the ledger store has no empty-topic guard).
 *
 * @param topic - the tokened ledger topic the server minted.
 * @param onNew - called once per new item; typically fires a `toast(...)`.
 * @sideEffect Subscribes to `topic`; calls `onNew` for each unseen item.
 */
export function useToastLedger<T extends LedgerItem>(topic: string, onNew: (item: T) => void): void {
    const items = useLedger<T>(topic);
    const shownRef = useRef<Set<string>>(new Set());
    // Read the latest callback without re-running the effect on every render.
    const onNewRef = useRef(onNew);
    onNewRef.current = onNew;

    useEffect(() => {
        for (const item of items) {
            if (shownRef.current.has(item.id)) continue;
            shownRef.current.add(item.id);
            onNewRef.current(item);
        }
    }, [items]);
}
